import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  ClipboardCheck,
  Calendar,
  Download,
  Pen,
  Check,
  Lock,
  Eye,
  Clock,
  ChevronDown,
  ChevronRight,
  Wrench,
  Truck,
  MapPin,
  Building,
  Package,
  CheckCircle,
  Loader2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Signature Canvas Component
const SignatureCanvas = ({ onSave, existingSignature }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#1e293b";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const saveSignature = () => {
    if (!hasSignature) {
      toast.error("Please draw your signature first");
      return;
    }
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSave(dataUrl);
  };

  if (existingSignature) {
    return (
      <div className="border rounded-sm p-2 bg-slate-50">
        <img src={existingSignature} alt="Signature" className="max-h-20" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={400}
        height={150}
        className="border-2 rounded-sm w-full touch-none bg-white cursor-crosshair border-dashed border-slate-300"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={clearSignature}>
          Clear
        </Button>
        <Button type="button" size="sm" onClick={saveSignature}>
          <Check className="h-4 w-4 mr-1" />
          Confirm Signature
        </Button>
      </div>
    </div>
  );
};

const MyJobCards = () => {
  const navigate = useNavigate();
  const [allJobCards, setAllJobCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobCard, setSelectedJobCard] = useState(null);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [customerSignName, setCustomerSignName] = useState("");
  const [customerSignature, setCustomerSignature] = useState(null);
  const [signing, setSigning] = useState(false);
  const [profile, setProfile] = useState(null);
  // Track which venue groups are collapsed; default all open
  const [collapsedVenues, setCollapsedVenues] = useState({});

  const fetchData = async () => {
    try {
      const [jobCardsRes, profileRes] = await Promise.all([
        axios.get(`${API}/my/job-cards`, { withCredentials: true }),
        axios.get(`${API}/my/profile`, { withCredentials: true }),
      ]);
      setAllJobCards(jobCardsRes.data);
      setProfile(profileRes.data);
    } catch (error) {
      console.error("Error fetching job cards:", error);
      toast.error("Failed to load job cards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // ── Status badge ─────────────────────────────────────────────────────────
  const getStatusBadge = (status, isLocked, jobType) => {
    if (isLocked && jobType === "service") {
      return (
        <Badge className="bg-emerald-100 text-emerald-800 rounded-sm border-0 gap-1">
          <Lock className="h-3 w-3" /> Completed
        </Badge>
      );
    }
    const cfg = {
      // service
      pending:          { label: "Pending",          color: "bg-amber-100 text-amber-700",   icon: Clock },
      confirmed:        { label: "Confirmed",         color: "bg-cyan-100 text-cyan-700",     icon: Check },
      in_progress:      { label: "In Progress",       color: "bg-blue-100 text-blue-700",     icon: Loader2 },
      worker_signed:    { label: "Awaiting Signature",color: "bg-indigo-100 text-indigo-700", icon: Pen },
      completed:        { label: "Completed",         color: "bg-emerald-100 text-emerald-700",icon: CheckCircle },
      cancelled:        { label: "Cancelled",         color: "bg-red-100 text-red-600",        icon: XCircle },
      // rental
      reserved:         { label: "Reserved",          color: "bg-slate-100 text-slate-600",    icon: Clock },
      approved:         { label: "Approved",          color: "bg-cyan-100 text-cyan-700",      icon: Check },
      pending_delivery: { label: "Pending Delivery",  color: "bg-amber-100 text-amber-700",    icon: Truck },
      delivered:        { label: "Delivered",         color: "bg-violet-100 text-violet-700",  icon: Package },
      return_scheduled: { label: "Return Scheduled",  color: "bg-orange-100 text-orange-700",  icon: Clock },
      returned:         { label: "Returned",          color: "bg-emerald-100 text-emerald-700",icon: CheckCircle },
    };
    const c = cfg[status] || cfg.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} rounded-sm border-0 gap-1`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </Badge>
    );
  };

  // ── PDF download ─────────────────────────────────────────────────────────
  const downloadPdf = async (jobCard) => {
    try {
      const isRental = jobCard.job_type === "rental";
      const url = isRental
        ? `${API}/rental-job-cards/${jobCard.rental_id}/pdf`
        : `${API}/job-cards/${jobCard.booking_id}/pdf`;
      const response = await axios.get(url, { withCredentials: true, responseType: "blob" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(new Blob([response.data]));
      link.setAttribute("download", `job_card_${isRental ? jobCard.rental_id : jobCard.booking_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const openSignDialog = (jobCard) => {
    setSelectedJobCard(jobCard);
    setCustomerSignName(profile?.name || jobCard.customer_name || "");
    setCustomerSignature(null);
    setShowSignDialog(true);
  };

  const handleCustomerSign = async () => {
    if (!customerSignature) { toast.error("Please draw your signature"); return; }
    if (!customerSignName.trim()) { toast.error("Please enter your name"); return; }
    setSigning(true);
    try {
      await axios.post(
        `${API}/job-cards/${selectedJobCard.booking_id}/sign`,
        { signer: "customer", name: customerSignName, signature: customerSignature },
        { withCredentials: true }
      );
      toast.success("Job card signed successfully!");
      setShowSignDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sign job card");
    } finally {
      setSigning(false);
    }
  };

  // ── Group job cards by venue ─────────────────────────────────────────────
  const venueGroups = useMemo(() => {
    const groups = {};
    for (const jc of allJobCards) {
      const key = jc.venue_id || "__none__";
      const label = jc.venue_name || "No Venue Assigned";
      if (!groups[key]) groups[key] = { key, label, jobs: [] };
      groups[key].jobs.push(jc);
    }
    // Sort: named venues alphabetically first, "No Venue" last
    return Object.values(groups).sort((a, b) => {
      if (a.key === "__none__") return 1;
      if (b.key === "__none__") return -1;
      return a.label.localeCompare(b.label);
    });
  }, [allJobCards]);

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const ACTIVE   = ["pending","confirmed","in_progress","reserved","approved","pending_delivery"];
    const IN_FIELD = ["delivered","pending_return","return_scheduled"];
    const DONE     = ["completed","returned"];
    return {
      total:     allJobCards.length,
      active:    allJobCards.filter(j => ACTIVE.includes(j.status)).length,
      inField:   allJobCards.filter(j => IN_FIELD.includes(j.status)).length,
      completed: allJobCards.filter(j => DONE.includes(j.status)).length,
      pending:   allJobCards.filter(j => j.status === "worker_signed").length,
    };
  }, [allJobCards]);

  const toggleVenue = (key) =>
    setCollapsedVenues(prev => ({ ...prev, [key]: !prev[key] }));

  // ── Single job card row ───────────────────────────────────────────────────
  const JobRow = ({ jc }) => {
    const isRental = jc.job_type === "rental";
    const id = isRental ? jc.rental_id : jc.booking_id;
    const route = isRental ? `/rental-job-card/${id}` : `/job-card/${id}`;
    const title = isRental ? jc.item_name : (jc.service_name || "Service");
    const date  = isRental ? jc.start_date : jc.service_date;
    const needsSign = !isRental && jc.status === "worker_signed" && !jc.customer_signature?.confirmed;

    return (
      <div
        className="flex items-center justify-between py-3 px-4 hover:bg-slate-50 cursor-pointer rounded-sm group transition-colors"
        onClick={() => navigate(route)}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 ${isRental ? "bg-amber-50" : "bg-blue-50"}`}>
            {isRental
              ? <Truck className="h-4 w-4 text-amber-600" />
              : <Wrench className="h-4 w-4 text-blue-600" />}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-slate-900 text-sm truncate">{title}</p>
            <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />{date || "—"}
              </span>
              {jc.technician_name && <span>Tech: {jc.technician_name}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-3" onClick={(e) => e.stopPropagation()}>
          {getStatusBadge(jc.status, jc.is_locked, jc.job_type)}
          {needsSign && (
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 h-7 text-xs"
              onClick={() => openSignDialog(jc)}
            >
              <Pen className="h-3 w-3 mr-1" />Sign
            </Button>
          )}
          <Button
            variant="ghost" size="icon"
            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => downloadPdf(jc)}
            title="Download PDF"
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48" />
          <div className="space-y-4">
            {[1,2,3].map(i => <div key={i} className="h-32 bg-slate-200 rounded" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="my-job-cards-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
          <ClipboardCheck className="h-7 w-7" />
          My Job Cards
        </h1>
        <p className="text-slate-500 mt-1">All service and rental records for your account</p>
      </div>

      {/* Stats Bar */}
      {allJobCards.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Total",     value: stats.total,     color: "text-slate-800" },
            { label: "Active",    value: stats.active,    color: "text-blue-600" },
            { label: "In Field",  value: stats.inField,   color: "text-violet-600" },
            { label: "Completed", value: stats.completed, color: "text-emerald-600" },
          ].map(s => (
            <Card key={s.label} className="rounded-sm border-slate-200">
              <CardContent className="p-3 text-center">
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pending Signature Alert */}
      {stats.pending > 0 && (
        <Card className="rounded-sm border-indigo-200 bg-indigo-50 mb-6">
          <CardContent className="p-4 flex items-center gap-3">
            <Pen className="h-5 w-5 text-indigo-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-indigo-800">
                {stats.pending} job card{stats.pending > 1 ? "s" : ""} awaiting your signature
              </p>
              <p className="text-sm text-indigo-600">Please review and sign to confirm service completion</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Venue Groups */}
      {allJobCards.length > 0 ? (
        <div className="space-y-4">
          {venueGroups.map(group => {
            const isCollapsed = !!collapsedVenues[group.key];
            const isNoVenue = group.key === "__none__";
            return (
              <Card key={group.key} className="rounded-sm border-slate-200 overflow-hidden">
                {/* Venue Header */}
                <button
                  className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b ${isNoVenue ? "bg-slate-50 border-slate-200" : "bg-white border-slate-200"}`}
                  onClick={() => toggleVenue(group.key)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${isNoVenue ? "bg-slate-200" : "bg-blue-100"}`}>
                      {isNoVenue
                        ? <Building className="h-4 w-4 text-slate-500" />
                        : <MapPin className="h-4 w-4 text-blue-600" />}
                    </div>
                    <div>
                      <p className={`font-semibold text-sm ${isNoVenue ? "text-slate-500" : "text-slate-900"}`}>
                        {group.label}
                      </p>
                      <p className="text-xs text-slate-400">
                        {group.jobs.length} job{group.jobs.length !== 1 ? "s" : ""}
                        {" · "}
                        {group.jobs.filter(j => ["pending","in_progress","worker_signed","approved","pending_delivery"].includes(j.status)).length} active
                      </p>
                    </div>
                  </div>
                  {isCollapsed
                    ? <ChevronRight className="h-4 w-4 text-slate-400" />
                    : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </button>

                {/* Job Rows */}
                {!isCollapsed && (
                  <div className="divide-y divide-slate-100">
                    {group.jobs.map(jc => (
                      <JobRow key={jc.rental_id || jc.booking_id} jc={jc} />
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-10 text-center">
            <ClipboardCheck className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 font-medium">No job cards yet</p>
            <p className="text-sm text-slate-400 mt-1">
              Service and rental job cards will appear here once your bookings are confirmed
            </p>
          </CardContent>
        </Card>
      )}

      {/* Customer Sign Dialog */}
      <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Sign Job Card</DialogTitle>
            <DialogDescription>
              Please review and sign to confirm that the service was completed satisfactorily.
            </DialogDescription>
          </DialogHeader>
          {selectedJobCard && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-sm space-y-2 text-sm">
                <p className="font-medium">{selectedJobCard.service_name || "Service"}</p>
                <p className="text-slate-600">Date: {selectedJobCard.service_date}</p>
                <p className="text-slate-600">Technician: {selectedJobCard.technician_name || "—"}</p>
                {selectedJobCard.venue_name && (
                  <p className="text-blue-600 flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />{selectedJobCard.venue_name}
                  </p>
                )}
                {selectedJobCard.worker_signature && (
                  <p className="text-emerald-600 flex items-center gap-1">
                    <Check className="h-3.5 w-3.5" />Technician has signed
                  </p>
                )}
              </div>
              <div>
                <Label>Your Name</Label>
                <Input
                  value={customerSignName}
                  onChange={(e) => setCustomerSignName(e.target.value)}
                  placeholder="Enter your name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Your Signature</Label>
                <SignatureCanvas
                  onSave={(sig) => setCustomerSignature(sig)}
                  existingSignature={customerSignature}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignDialog(false)}>Cancel</Button>
            <Button onClick={handleCustomerSign} disabled={signing || !customerSignature}>
              {signing ? "Signing..." : "Confirm & Sign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyJobCards;
